import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useScannerContext } from "@/hooks/scanner-context";
import {
  useViewerHasAtLeastAdminRights,
  useViewerHasAtLeastLogisticianRights,
} from "@/lib/rights/use-viewer-has-right";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useLineItemsState, {
  LineItemStateKey,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItems } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { useRejectLineItems } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";
import useRejectLineItemsState from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";
import { useReturnRejectionContext } from "@/pages/(app)/returns/view/utils/return-rejection-context";
import { useReturnValidationContext } from "@/pages/(app)/returns/view/utils/return-validation-context";
import useGetItemsInActiveReturns, {
  ItemInActiveReturn,
} from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import useEditLineItem from "@babackapp/private-api-client/client/use-edit-line-item";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { useCanLogisticianRevalidateAfterMerchant } from "@/pages/(app)/returns/hooks/use-can-logistician-revalidate-after-merchant";
import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "@babackapp/calculations/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { toast } from "@babackapp/ui/components/sonner";
import useTranslation from "@babackapp/translations/use-translation";
import { useEffect, useRef } from "react";

const statesWithActiveReturnConflictCheck = new Set<LineItemStateKey>([
  "not-treated-by-merchant",
  "not-returned",
]);

export function getScannableLineItemStateKey(
  lineItem: ReturnRequestLineItemDto,
  workflowLogisticianActivated: boolean,
  // BAB-2943: keeps merchant-treated items scannable for the logistician.
  allowRevalidationAfterMerchant = false,
): LineItemStateKey | null {
  if (
    lineItem.quantity === 0 &&
    getLineItemNotReturnedQuantity({ lineItem }) > 0
  ) {
    return "not-returned";
  }

  if (workflowLogisticianActivated) {
    if (
      getLineItemNotTreatedByLogisticianQuantity({
        lineItem,
        allowRevalidationAfterMerchant,
      }) > 0
    ) {
      return "not-treated-by-logistician";
    }
    if (
      getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician(
        { lineItem },
      ) > 0
    ) {
      return "validated-by-logistician";
    }
    return null;
  }

  if (
    getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician(
      { lineItem },
    ) > 0
  ) {
    return "not-treated-by-merchant";
  }
  return null;
}

export function getScannerAdjustedMaxQuantity({
  lineItemId,
  stateKey,
  maxQuantity,
  itemsInActiveReturns,
}: {
  lineItemId: string;
  stateKey: LineItemStateKey;
  maxQuantity: number;
  itemsInActiveReturns: ItemInActiveReturn[] | undefined;
}): number {
  if (!statesWithActiveReturnConflictCheck.has(stateKey)) {
    return maxQuantity;
  }

  const quantityInOtherReturns =
    itemsInActiveReturns
      ?.filter((item) => item.lineItemId === lineItemId)
      .reduce((sum, item) => sum + item.quantity, 0) ?? 0;

  return Math.max(0, maxQuantity - quantityInOtherReturns);
}

type ScannerQuantityState = {
  selectedQuantity: number;
  maxQuantity: number;
};

export type ScannerLineItemsState = Record<
  string,
  Partial<Record<LineItemStateKey, ScannerQuantityState>>
>;

export type ScannerMatch = {
  lineItem: ReturnRequestLineItemDto;
  stateKey: LineItemStateKey;
  selectedQuantity: number;
  adjustedMaxQuantity: number;
};

export function isLineItemMatchingCode(
  lineItem: ReturnRequestLineItemDto,
  code: string,
): boolean {
  return lineItem.variant?.barcode === code || lineItem.sku === code;
}

/**
 * Sections of both dialogs are permission-gated (see the articles-lists and
 * articles-reject-lists components), so a scan must never add a quantity to a
 * section the viewer cannot see: it would stay invisible and still be
 * submitted, because both submits sum every state key of the line item.
 *
 * The gates are the same in both dialogs, except for `not-returned` which the
 * validation dialog shows to every role.
 */
export function isStateKeyVisibleToViewer({
  stateKey,
  dialog,
  hasAdminRights,
  hasLogisticianRights,
}: {
  stateKey: LineItemStateKey;
  dialog: "validation" | "rejection";
  hasAdminRights: boolean;
  hasLogisticianRights: boolean;
}): boolean {
  switch (stateKey) {
    case "not-treated-by-logistician":
      return hasLogisticianRights;
    case "not-returned":
      return dialog === "validation" ? true : hasAdminRights;
    default:
      return hasAdminRights;
  }
}

/**
 * Finds the line item a scanned code should add a quantity to, in either the
 * validation or the rejection quantity state.
 *
 * `isQuantitySelectionStarted` tells whether the already selected quantities
 * count: when a dialog is open we add to what is displayed, when none is open
 * we start a fresh selection at 0.
 *
 * `isStateKeyVisible` keeps the scan out of the sections the viewer cannot see.
 */
export function findScannableLineItem({
  code,
  lineItems,
  lineItemsState,
  workflowLogisticianActivated,
  allowRevalidationAfterMerchant,
  itemsInActiveReturns,
  isQuantitySelectionStarted,
  isStateKeyVisible,
}: {
  code: string;
  lineItems: ReturnRequestLineItemDto[];
  lineItemsState: ScannerLineItemsState;
  workflowLogisticianActivated: boolean;
  allowRevalidationAfterMerchant: boolean;
  itemsInActiveReturns: ItemInActiveReturn[] | undefined;
  isQuantitySelectionStarted: boolean;
  isStateKeyVisible: (stateKey: LineItemStateKey) => boolean;
}): ScannerMatch | null {
  for (const lineItem of lineItems) {
    if (!isLineItemMatchingCode(lineItem, code)) continue;

    const stateKey = getScannableLineItemStateKey(
      lineItem,
      workflowLogisticianActivated,
      allowRevalidationAfterMerchant,
    );
    if (!stateKey) continue;
    if (!isStateKeyVisible(stateKey)) continue;
    if (
      statesWithActiveReturnConflictCheck.has(stateKey) &&
      !itemsInActiveReturns
    ) {
      continue;
    }

    const itemState = lineItemsState[lineItem.line_item_id]?.[stateKey];
    if (!itemState) continue;

    const adjustedMaxQuantity = getScannerAdjustedMaxQuantity({
      lineItemId: lineItem.line_item_id,
      stateKey,
      maxQuantity: itemState.maxQuantity,
      itemsInActiveReturns,
    });
    const selectedQuantity = isQuantitySelectionStarted
      ? itemState.selectedQuantity
      : 0;

    if (selectedQuantity >= adjustedMaxQuantity) continue;

    return { lineItem, stateKey, selectedQuantity, adjustedMaxQuantity };
  }

  return null;
}

export function useArticleScanner() {
  const { registerInterceptor } = useScannerContext();
  const t = useTranslation();
  const returnRequest = useCurrentReturnRequest();
  const { data: shop } = useCurrentShopSuspense();
  const canRevalidateAfterMerchant = useCanLogisticianRevalidateAfterMerchant();
  const hasAdminRights = useViewerHasAtLeastAdminRights();
  const hasLogisticianRights = useViewerHasAtLeastLogisticianRights();
  const { isOpen, openDialog } = useReturnValidationContext();
  const {
    isOpen: isRejectionOpen,
    notifyScannedStateKey: notifyRejectionScannedStateKey,
  } = useReturnRejectionContext();
  const { lineItemsState, setLineItemState, resetAllQuantities } =
    useLineItemsState();
  const {
    lineItemsState: rejectLineItemsState,
    setLineItemState: setRejectLineItemState,
  } = useRejectLineItemsState();
  const { items, setItems } = useRefundLineItems();
  const { items: rejectItems, setItems: setRejectItems } = useRejectLineItems();
  const { data: itemsInActiveReturns } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
  });
  const { mutate: editLineItem } = useEditLineItem({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
    onSuccess: (updatedReturnRequest) => {
      setItems(updatedReturnRequest.line_items);
    },
  });
  const { mutate: editRejectLineItem } = useEditLineItem({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
    onSuccess: (updatedReturnRequest) => {
      setRejectItems(updatedReturnRequest.line_items);
    },
  });

  const isOpenRef = useRef(isOpen);
  const isRejectionOpenRef = useRef(isRejectionOpen);
  const lineItemsStateRef = useRef(lineItemsState);
  const rejectLineItemsStateRef = useRef(rejectLineItemsState);
  const returnRequestRef = useRef(returnRequest);
  const workflowLogisticianRef = useRef(shop.workflowLogistician.activated);
  const canRevalidateAfterMerchantRef = useRef(canRevalidateAfterMerchant);
  const hasAdminRightsRef = useRef(hasAdminRights);
  const hasLogisticianRightsRef = useRef(hasLogisticianRights);
  const itemsRef = useRef(items);
  const rejectItemsRef = useRef(rejectItems);
  const itemsInActiveReturnsRef = useRef(
    itemsInActiveReturns?.itemsInOtherReturns,
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    isRejectionOpenRef.current = isRejectionOpen;
  }, [isRejectionOpen]);
  useEffect(() => {
    lineItemsStateRef.current = lineItemsState;
  }, [lineItemsState]);
  useEffect(() => {
    rejectLineItemsStateRef.current = rejectLineItemsState;
  }, [rejectLineItemsState]);
  useEffect(() => {
    returnRequestRef.current = returnRequest;
  }, [returnRequest]);
  useEffect(() => {
    workflowLogisticianRef.current = shop.workflowLogistician.activated;
  }, [shop.workflowLogistician.activated]);
  useEffect(() => {
    canRevalidateAfterMerchantRef.current = canRevalidateAfterMerchant;
  }, [canRevalidateAfterMerchant]);
  useEffect(() => {
    hasAdminRightsRef.current = hasAdminRights;
  }, [hasAdminRights]);
  useEffect(() => {
    hasLogisticianRightsRef.current = hasLogisticianRights;
  }, [hasLogisticianRights]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    rejectItemsRef.current = rejectItems;
  }, [rejectItems]);
  useEffect(() => {
    itemsInActiveReturnsRef.current = itemsInActiveReturns?.itemsInOtherReturns;
  }, [itemsInActiveReturns?.itemsInOtherReturns]);

  const setLineItemStateRef = useRef(setLineItemState);
  setLineItemStateRef.current = setLineItemState;
  const setRejectLineItemStateRef = useRef(setRejectLineItemState);
  setRejectLineItemStateRef.current = setRejectLineItemState;
  const resetAllQuantitiesRef = useRef(resetAllQuantities);
  resetAllQuantitiesRef.current = resetAllQuantities;
  const openDialogRef = useRef(openDialog);
  openDialogRef.current = openDialog;
  const notifyRejectionScannedStateKeyRef = useRef(
    notifyRejectionScannedStateKey,
  );
  notifyRejectionScannedStateKeyRef.current = notifyRejectionScannedStateKey;
  const setItemsRef = useRef(setItems);
  setItemsRef.current = setItems;
  const setRejectItemsRef = useRef(setRejectItems);
  setRejectItemsRef.current = setRejectItems;
  const editLineItemRef = useRef(editLineItem);
  editLineItemRef.current = editLineItem;
  const editRejectLineItemRef = useRef(editRejectLineItem);
  editRejectLineItemRef.current = editRejectLineItem;

  const setDefaultResolutionRef = useRef(
    (lineItem: ReturnRequestLineItemDto) => {
      if (lineItem.resolution) return;

      setItemsRef.current(
        itemsRef.current.map((item) =>
          item.line_item_id === lineItem.line_item_id
            ? { ...item, resolution: "refund" }
            : item,
        ),
      );
      editLineItemRef.current({
        lineItemId: lineItem.line_item_id,
        requestBody: { resolution: "refund" },
      });
    },
  );

  /**
   * Mirrors what the rejection dialog does when a quantity is picked on an
   * article without resolution (see articles-reject-lists components).
   */
  const setDefaultRejectionResolutionRef = useRef(
    (lineItem: ReturnRequestLineItemDto) => {
      const rejectItem = rejectItemsRef.current.find(
        (item) => item.line_item_id === lineItem.line_item_id,
      );
      if ((rejectItem ?? lineItem).resolution) return;

      setRejectItemsRef.current(
        rejectItemsRef.current.map((item) =>
          item.line_item_id === lineItem.line_item_id
            ? { ...item, resolution: "refund" }
            : item,
        ),
      );
      editRejectLineItemRef.current({
        lineItemId: lineItem.line_item_id,
        requestBody: { resolution: "refund" },
      });
    },
  );

  useEffect(() => {
    const interceptor = (code: string): boolean => {
      const lineItems = returnRequestRef.current.line_items;
      const isValidationPopupOpen = isOpenRef.current;
      const isRejectionPopupOpen = isRejectionOpenRef.current;
      const workflowLogisticianActivated = workflowLogisticianRef.current;
      const canRevalidate = canRevalidateAfterMerchantRef.current;
      const itemsInActiveReturns = itemsInActiveReturnsRef.current;

      const belongsToReturn = lineItems.some((lineItem) =>
        isLineItemMatchingCode(lineItem, code),
      );

      const isStateKeyVisibleIn =
        (dialog: "validation" | "rejection") => (stateKey: LineItemStateKey) =>
          isStateKeyVisibleToViewer({
            stateKey,
            dialog,
            hasAdminRights: hasAdminRightsRef.current,
            hasLogisticianRights: hasLogisticianRightsRef.current,
          });

      /**
       * The rejection dialog owns the scanner while it is open: we never open
       * the validation dialog behind it, we only add a quantity to the scanned
       * article inside the rejection dialog.
       */
      if (isRejectionPopupOpen) {
        const rejectionMatch = findScannableLineItem({
          code,
          lineItems,
          lineItemsState: rejectLineItemsStateRef.current,
          workflowLogisticianActivated,
          allowRevalidationAfterMerchant: canRevalidate,
          itemsInActiveReturns,
          isQuantitySelectionStarted: true,
          isStateKeyVisible: isStateKeyVisibleIn("rejection"),
        });

        if (!rejectionMatch) {
          if (!belongsToReturn) {
            toast.error(t("scannerMode.unexpectedArticle"));
          }
          return true;
        }

        setRejectLineItemStateRef.current(
          rejectionMatch.lineItem.line_item_id,
          rejectionMatch.stateKey,
          rejectionMatch.selectedQuantity + 1,
        );
        if (
          rejectionMatch.stateKey === "not-returned" ||
          rejectionMatch.stateKey === "not-treated-by-logistician"
        ) {
          setDefaultRejectionResolutionRef.current(rejectionMatch.lineItem);
        }
        notifyRejectionScannedStateKeyRef.current(rejectionMatch.stateKey);

        return true;
      }

      const matched = findScannableLineItem({
        code,
        lineItems,
        lineItemsState: lineItemsStateRef.current,
        workflowLogisticianActivated,
        allowRevalidationAfterMerchant: canRevalidate,
        itemsInActiveReturns,
        isQuantitySelectionStarted: isValidationPopupOpen,
        isStateKeyVisible: isStateKeyVisibleIn("validation"),
      });

      if (!matched) {
        if (isValidationPopupOpen && !belongsToReturn) {
          toast.error(t("scannerMode.unexpectedArticle"));
        }
        return isValidationPopupOpen || belongsToReturn;
      }

      const { lineItem, stateKey, selectedQuantity } = matched;

      if (!isValidationPopupOpen) {
        resetAllQuantitiesRef.current();
        setLineItemStateRef.current(lineItem.line_item_id, stateKey, 1);
        if (stateKey === "not-returned") {
          setDefaultResolutionRef.current(lineItem);
        }
        openDialogRef.current({ openedByScanner: true, stateKey });
      } else {
        setLineItemStateRef.current(
          lineItem.line_item_id,
          stateKey,
          selectedQuantity + 1,
        );
        if (stateKey === "not-returned") {
          setDefaultResolutionRef.current(lineItem);
        }
      }

      return true;
    };

    return registerInterceptor(interceptor);
  }, [registerInterceptor]);
}
